import { useMemo } from "react";
import { useFloatingConversationMessages } from "@/desktop/use-floating-conversation-messages";
import { useConversationStore } from "@/stores/conversation-store";
import { isMessageVisibleInConversationFrame } from "./conversation-frame-utils";
import { useConversationFrameHistoryHydration } from "./use-conversation-frame-history";
import { useConversationFrameLoadingState } from "./use-conversation-frame-loading-state";
import type { DetachedConversationFrameOptions } from "./conversation-frame-types";

export function useConversationFrameMessages({
  frameId,
  conversationId,
  executionId,
  runtimeSessionId,
  requireExactIdentity,
  detached,
  isNewPending,
}: {
  frameId: string;
  conversationId?: string;
  executionId?: string;
  runtimeSessionId?: string;
  requireExactIdentity: boolean;
  detached?: DetachedConversationFrameOptions;
  isNewPending: boolean;
}) {
  const messages = useConversationStore((state) => state.messages);
  const detachedMessages = detached?.messages;
  const frameMessages = useMemo(
    () =>
      detachedMessages ??
      messages.filter((message) =>
        isMessageVisibleInConversationFrame(message, {
          conversationId,
          id: frameId,
          executionId,
          runtimeSessionId,
          requireExactIdentity,
        }),
      ),
    [
      conversationId,
      detachedMessages,
      executionId,
      frameId,
      messages,
      requireExactIdentity,
      runtimeSessionId,
    ],
  );
  const floatingMessages = useFloatingConversationMessages(
    frameId,
    conversationId,
    frameMessages,
  );
  const displayedFrameMessages = useMemo(
    () => (isNewPending ? [] : floatingMessages),
    [floatingMessages, isNewPending],
  );
  useConversationFrameHistoryHydration({
    enabled: !isNewPending && !detached,
    frameId,
    conversationId,
  });
  const { showLoadingPlaceholder } = useConversationFrameLoadingState({
    frameId,
    conversationId,
    visibleMessageCount: displayedFrameMessages.length,
  });
  return {
    displayedFrameMessages,
    showLoadingPlaceholder:
      !isNewPending && !detached && showLoadingPlaceholder,
  };
}
